import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";
import i18n from "@/i18n";
import api from "@/utils/axios";
import cls from "./CitizenshipType.module.scss";

export default function CitizenshipType() {
  const navigate = useNavigate();

  const t = (key, options) => i18n.t(key, options);

  const citizenships = useMemo(
    () => [
      {
        key: "explorer_citizen",
        theme: "explorer",
        badge: "E",
        name: t("citizenshipType.cards.explorer.name"),
        label: t("citizenshipType.cards.explorer.label"),
        price: t("citizenshipType.cards.explorer.price"),
        bonus: t("citizenshipType.cards.explorer.bonus"),
        tagline: t("citizenshipType.cards.explorer.tagline"),
        verification: t("citizenshipType.cards.explorer.verification"),
        features: t("citizenshipType.cards.explorer.features", {
          returnObjects: true,
        }),
      },
      {
        key: "nexus_citizen",
        theme: "nexus",
        badge: "N",
        name: t("citizenshipType.cards.nexus.name"),
        label: t("citizenshipType.cards.nexus.label"),
        price: t("citizenshipType.cards.nexus.price"),
        bonus: t("citizenshipType.cards.nexus.bonus"),
        tagline: t("citizenshipType.cards.nexus.tagline"),
        verification: t("citizenshipType.cards.nexus.verification"),
        features: t("citizenshipType.cards.nexus.features", {
          returnObjects: true,
        }),
      },
      {
        key: "lumora_citizen",
        theme: "lumora",
        badge: "L",
        name: t("citizenshipType.cards.lumora.name"),
        label: t("citizenshipType.cards.lumora.label"),
        price: t("citizenshipType.cards.lumora.price"),
        bonus: t("citizenshipType.cards.lumora.bonus"),
        tagline: t("citizenshipType.cards.lumora.tagline"),
        verification: t("citizenshipType.cards.lumora.verification"),
        features: t("citizenshipType.cards.lumora.features", {
          returnObjects: true,
        }),
      },
    ],
    [],
  );

  const [selected, setSelected] = useState("nexus_citizen");
  const [submitting, setSubmitting] = useState(false);

  const selectedItem = citizenships.find((item) => item.key === selected);

  const handleSubmit = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);

      const { data } = await api.post("/v1/member/citizenship/select", {
        citizenshipType: selected,
      });

      navigate(data?.nextStep || "/member/local", { replace: true });
    } catch (error) {
      const message =
        error?.response?.data?.message || t("citizenshipType.errors.save");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`${cls.page} ${cls[selectedItem?.theme || "nexus"]}`}>
      <div className={cls.orbOne} />
      <div className={cls.orbTwo} />
      <div className={cls.grid} />

      <header className={cls.hero}>
        <span>{t("citizenshipType.kicker")}</span>
        <h1>{t("citizenshipType.title")}</h1>
        <p>{t("citizenshipType.subtitle")}</p>
      </header>

      <div className={cls.cards}>
        {citizenships.map((item, index) => {
          const isSelected = selected === item.key;

          return (
            <Motion.button
              type="button"
              key={item.key}
              className={`${cls.card} ${cls[item.theme]} ${
                isSelected ? cls.selected : ""
              } ${index === 1 ? cls.middle : ""}`}
              onClick={() => setSelected(item.key)}
              whileTap={{ scale: 0.97 }}
            >
              <div className={cls.top}>
                <div className={cls.badge}>{item.badge}</div>

                <div>
                  <span>{item.label}</span>
                  <h2>{item.name}</h2>
                </div>
              </div>

              <div className={cls.priceRow}>
                <strong className={cls.price}>{item.price}</strong>
                <strong className={cls.bonus}>{item.bonus}</strong>
              </div>

              <p className={cls.tagline}>{item.tagline}</p>

              <ul>
                {item.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>

              <div className={cls.verify}>
                <small>{t("citizenshipType.verificationTitle")}</small>
                <p>{item.verification}</p>
              </div>

              <div className={cls.selectMark}>
                {isSelected
                  ? t("citizenshipType.selected")
                  : t("citizenshipType.tapToSelect")}
              </div>
            </Motion.button>
          );
        })}
      </div>

      <div
        className={`${cls.submitBox} ${cls[selectedItem?.theme || "nexus"]}`}
      >
        <div>
          <span>{t("citizenshipType.selectedTitle")}</span>
          <strong>{selectedItem?.name}</strong>
          <p>{selectedItem?.bonus}</p>
        </div>

        <button type="button" onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? t("citizenshipType.saving")
            : t("citizenshipType.submit", { name: selectedItem?.name })}
        </button>
      </div>
    </section>
  );
}
